import React from 'react';
import {
  Divider,
  Form,
  FormInstance,
  Input,
  InputNumber,
  Select,
  Typography,
} from 'antd';
import LocationPicker from '../LocationPicker';
import type { LocationPayload } from '../../types/location';
import { AdminImageUpload } from './AdminImageUpload';
import {
  MerchantOnboardingFormContent,
  DEFAULT_PROMO_CREDITS,
} from './MerchantOnboardingFormContent';

const { Text } = Typography;

const VN_BANKS = [
  { code: 'VCB', name: 'Vietcombank' },
  { code: 'TCB', name: 'Techcombank' },
  { code: 'BIDV', name: 'BIDV' },
  { code: 'VTB', name: 'VietinBank' },
  { code: 'ACB', name: 'ACB' },
  { code: 'MB', name: 'MB Bank' },
  { code: 'VPB', name: 'VPBank' },
  { code: 'TPB', name: 'TPBank' },
  { code: 'STB', name: 'Sacombank' },
  { code: 'HDB', name: 'HDBank' },
];

const DAY_LABELS = [
  'Thứ 2',
  'Thứ 3',
  'Thứ 4',
  'Thứ 5',
  'Thứ 6',
  'Thứ 7',
  'Chủ nhật',
];

export type MerchantRegistrationKycFormContentProps = {
  form: FormInstance;
  location: LocationPayload | null;
  onLocationChange: (payload: LocationPayload) => void;
  locationPickerKey?: string | number;
  initialAddress?: string;
};

export function MerchantRegistrationKycFormContent({
  form,
  location,
  onLocationChange,
  locationPickerKey,
  initialAddress = '',
}: MerchantRegistrationKycFormContentProps) {
  const idFrontUrl = Form.useWatch(['representative', 'idFrontImageUrl'], form);
  const idBackUrl = Form.useWatch(['representative', 'idBackImageUrl'], form);
  const avatarUrl = Form.useWatch(['photos', 'avatarUrl'], form);

  return (
    <>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Lưu để ghi nhận thông tin KYC. Bấm <strong>Duyệt</strong> khi đã xác
        minh đầy đủ thông tin. Tài khoản AppotaPay sẽ được cấp sau bởi vận hành.
      </Text>

      <Divider orientation="left">Tài khoản & quán</Divider>
      <MerchantOnboardingFormContent
        form={form}
        location={location}
        onLocationChange={onLocationChange}
        locationPickerKey={locationPickerKey}
        initialAddress={initialAddress}
        showPromoCreditsField
        deferMapMs={300}
      />

      <Divider orientation="left">Địa chỉ hành chính (KYC)</Divider>
      <Form.Item name="provinceName" label="Tỉnh/Thành phố">
        <Input placeholder="Ví dụ: TP. Hồ Chí Minh" />
      </Form.Item>
      <Form.Item name="wardName" label="Phường/Xã">
        <Input placeholder="Ví dụ: Phường Bến Nghé" />
      </Form.Item>
      <Form.Item name="street" label="Đường / số nhà">
        <Input placeholder="Tự điền hoặc lấy từ bản đồ" />
      </Form.Item>

      <Divider orientation="left">Người đại diện (CCCD)</Divider>
      <Form.Item
        name={['representative', 'fullName']}
        label="Họ tên đầy đủ"
        rules={[{ required: true, message: 'Bắt buộc khi duyệt' }]}
      >
        <Input placeholder="Trùng tên trên CCCD" />
      </Form.Item>
      <Form.Item
        name={['representative', 'email']}
        label="Email đại diện"
        rules={[{ type: 'email', message: 'Email không hợp lệ' }]}
      >
        <Input type="email" placeholder="email@example.com" />
      </Form.Item>
      <Form.Item name={['representative', 'phone']} label="SĐT đại diện">
        <Input placeholder="090..." />
      </Form.Item>
      <Form.Item
        name={['representative', 'idNumber']}
        label="Số CCCD/CMND"
        rules={[{ pattern: /^\d{6,12}$/, message: '6–12 chữ số' }]}
      >
        <Input placeholder="012345678901" />
      </Form.Item>
      <Form.Item name={['representative', 'idIssueDate']} label="Ngày cấp">
        <Input type="date" />
      </Form.Item>
      <Form.Item name={['representative', 'idIssuePlace']} label="Nơi cấp">
        <Input placeholder="Cục Cảnh sát..." />
      </Form.Item>
      <div
        style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 16 }}
      >
        <Form.Item name={['representative', 'idFrontImageUrl']} hidden>
          <Input />
        </Form.Item>
        <Form.Item name={['representative', 'idBackImageUrl']} hidden>
          <Input />
        </Form.Item>
        <AdminImageUpload
          label="Ảnh mặt trước CCCD"
          value={idFrontUrl}
          onChange={url =>
            form.setFieldValue(['representative', 'idFrontImageUrl'], url)
          }
        />
        <AdminImageUpload
          label="Ảnh mặt sau CCCD"
          value={idBackUrl}
          onChange={url =>
            form.setFieldValue(['representative', 'idBackImageUrl'], url)
          }
        />
      </div>

      <Divider orientation="left">Tài khoản ngân hàng</Divider>
      <Form.Item name={['bank', 'bankCode']} label="Mã ngân hàng">
        <Select
          allowClear
          showSearch
          placeholder="Chọn ngân hàng"
          options={VN_BANKS.map(b => ({
            value: b.code,
            label: `${b.code} — ${b.name}`,
          }))}
          onChange={code => {
            const bank = VN_BANKS.find(b => b.code === code);
            if (bank) {
              form.setFieldValue(['bank', 'bankName'], bank.name);
            }
          }}
        />
      </Form.Item>
      <Form.Item name={['bank', 'bankName']} label="Tên ngân hàng" hidden>
        <Input />
      </Form.Item>
      <Form.Item name={['bank', 'accountNumber']} label="Số tài khoản">
        <Input placeholder="Số TK nhận tiền" />
      </Form.Item>
      <Form.Item name={['bank', 'accountName']} label="Tên chủ tài khoản">
        <Input placeholder="VIẾT HOA KHÔNG DẤU" />
      </Form.Item>
      <Form.Item name={['bank', 'branch']} label="Chi nhánh (tuỳ chọn)">
        <Input />
      </Form.Item>

      <Divider orientation="left">Ảnh quán</Divider>
      <Form.Item name={['photos', 'avatarUrl']} hidden>
        <Input />
      </Form.Item>
      <AdminImageUpload
        label="Ảnh đại diện quán"
        value={avatarUrl}
        onChange={url => form.setFieldValue(['photos', 'avatarUrl'], url)}
      />

      <Divider orientation="left">Giờ hoạt động (mặc định 08:00–22:00)</Divider>
      {DAY_LABELS.map((label, idx) => (
        <div
          key={label}
          style={{
            display: 'grid',
            gridTemplateColumns: '100px 1fr 1fr',
            gap: 8,
            marginBottom: 8,
            alignItems: 'center',
          }}
        >
          <Text>{label}</Text>
          <Form.Item
            name={['hours', 'days', idx, 'startTime']}
            noStyle
            initialValue="08:00"
          >
            <Input placeholder="08:00" />
          </Form.Item>
          <Form.Item
            name={['hours', 'days', idx, 'endTime']}
            noStyle
            initialValue="22:00"
          >
            <Input placeholder="22:00" />
          </Form.Item>
          <Form.Item
            name={['hours', 'days', idx, 'dayOfWeek']}
            hidden
            initialValue={idx + 1}
          >
            <InputNumber />
          </Form.Item>
          <Form.Item
            name={['hours', 'days', idx, 'enabled']}
            hidden
            initialValue={true}
          >
            <Input />
          </Form.Item>
        </div>
      ))}

      <Divider orientation="left">Ghi chú</Divider>
      <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
        Sau khi duyệt, vận hành liên hệ AppotaPay để tạo tài khoản REFER cho
        merchant. Khi nhận được credentials (Partner Code, API Key, Secret Key),
        nhập vào mục quản lý tài khoản AppotaPay của merchant.
      </Text>
    </>
  );
}

export { DEFAULT_PROMO_CREDITS };
